// material
import { Box, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';

import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppFlight,
  AppDistance,
  AppVolHours,
  AppAeros,
  AppReportMap,
  AppPhasesSummary,
  AppReportGraph,
  AppTotalPointSummary,
  AppPointsInfo
} from '../components/_dashboard/app';
// components
import Page from '../components/Page';
import { ReportFilterSidebar } from '../components/sidebarFilter';
import { getReports, getReportsDeviceIds } from '../fetch';
import { useTranslation } from '../common/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { summaryAction } from '../store';
import {
  selectDevicesArray,
  selectGroupSelectedId,
  selectSession,
  selectSummaryArray,
  selectSummaryFrom,
  selectSummaryPeriod,
  selectSummaryTo
} from '../createSelectorUtils';
// ----------------------------------------------------------------------

export default function DashboardApp() {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission =
    session.permission && session.permission.length !== 0
      ? session.permission.find((item) => item.groupid === groupId) || {}
      : {};

  const summary = useSelector(selectSummaryArray);
  const devices = useSelector(selectDevicesArray);

  const selectPeriod = useSelector(selectSummaryPeriod);
  const selectFrom = useSelector(selectSummaryFrom);
  const selectTo = useSelector(selectSummaryTo);

  const [openProgress, setOpenProgress] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const formik = useFormik({
    initialValues: {
      group: !session.administrator ? { id: groupId } : null,
      device: null,
      user: null,
      period:
        selectPeriod ||
        (permission.status === 3 || permission.status === 4 ? 'thisYear' : 'last3Month'),
      from: moment(selectFrom).isValid() ? moment(selectFrom) : moment().subtract(1, 'day'),
      to: moment(selectFrom).isValid() ? moment(selectTo) : moment(),
      displayFrom: moment(selectFrom).isValid() ? moment(selectFrom) : moment().subtract(1, 'day'),
      displayTo: moment(selectFrom).isValid() ? moment(selectTo) : moment()
    },
    onSubmit: async (values) => {
      setOpenProgress(true);
      let selectedFrom;
      let selectedTo;
      switch (values.period) {
        case 'today':
          selectedFrom = moment().startOf('day');
          selectedTo = moment().endOf('day');
          break;
        case 'yesterday':
          selectedFrom = moment().subtract(1, 'day').startOf('day');
          selectedTo = moment().subtract(1, 'day').endOf('day');
          break;
        case 'thisWeek':
          selectedFrom = moment().startOf('week');
          selectedTo = moment().endOf('week');
          break;
        case 'previousWeek':
          selectedFrom = moment().subtract(1, 'week').startOf('week');
          selectedTo = moment().subtract(1, 'week').endOf('week');
          break;
        case 'thisMonth':
          selectedFrom = moment().startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'previousMonth':
          selectedFrom = moment().subtract(1, 'month').startOf('month');
          selectedTo = moment().subtract(1, 'month').endOf('month');
          break;
        case 'last3Month':
          selectedFrom = moment().subtract(3, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'last6Month':
          selectedFrom = moment().subtract(6, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'thisYear':
          selectedFrom = moment().startOf('year');
          selectedTo = moment().endOf('month');
          break;
        default:
          selectedFrom = values.from.set('hour', 0).set('minute', 0).set('second', 0).utc(true);
          selectedTo = values.to.set('hour', 23).set('minute', 59).set('second', 59).utc(true);
          break;
      }

      setFieldValue('displayFrom', selectedFrom);
      setFieldValue('displayTo', selectedTo);
      dispatch(summaryAction.updatePeriod(values.period));
      dispatch(summaryAction.updateFrom(await selectedFrom));
      dispatch(summaryAction.updateTo(await selectedTo));

      let response;
      if (
        groupId === 0 &&
        (values.group === null || values.group.id === 0) &&
        values.device === null
      ) {
        let deviceIds = '';
        devices
          .filter(
            (device) =>
              device.groupId === 0 ||
              session.permission.find((item) => item.groupid === device.groupId) === undefined
          )
          .map((item) => (deviceIds += `deviceId=${item.id}&`));
        response = await getReportsDeviceIds(
          'summary',
          deviceIds,
          selectedFrom.toISOString(),
          selectedTo.toISOString(),
          false
        );
      } else {
        response = await getReports(
          'summary',
          values.device !== null ? values.device.id : 0,
          values.group !== null && values.device === null ? values.group.id : 0,
          values.user !== null ? values.user.id : 0,
          selectedFrom.toISOString(),
          selectedTo.toISOString(),
          false
        );
      }

      if (response.ok) {
        const result = await response.json();
        dispatch(summaryAction.update(await result));

        if (result.length === 0) {
          enqueueSnackbar(t('tableEmpty_Snackbar'), {
            variant: 'warning'
          });
        }
      } else {
        dispatch(summaryAction.update(await []));
        enqueueSnackbar(t('tableEmpty_Snackbar'), {
          variant: 'warning'
        });
      }

      setOpenFilter(false);
      setOpenProgress(false);
    }
  });
  const { resetForm, handleSubmit, setFieldValue } = formik;

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    handleSubmit();
    resetForm();
  };

  useEffectAsync(async () => {
    await resetForm();
    setFieldValue('group', !session.administrator ? { id: groupId } : null);
    setFieldValue('user', null);
  }, [groupId]);

  useEffectAsync(async () => {
    if (summary.length === 0) {
      await handleSubmit();
    }
  }, [groupId, devices.length]);
  // ----------------------------------------------------------------------
  return (
    <Page title={t('DB_Title')} openProgress={openProgress}>
      <Container maxWidth="xl">
        <Box sx={{ pb: 1 }}>
          <Typography variant="h4">{t('DB_Welcome')}</Typography>
        </Box>
        <Box sx={{ pb: 1 }}>
          <AppPointsInfo />
        </Box>
        <Stack
          direction="row"
          flexWrap="wrap-reverse"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 1, mt: 3 }}
        >
          <Chip
            label={`${t('words_From_M')} ${moment(formik.values.displayFrom).format(
              'DD/MM/YYYY'
            )} ${t('words_To')} ${moment(formik.values.displayTo).format('DD/MM/YYYY')}`}
            variant="outlined"
          />

          <Stack direction="row" spacing={1} flexShrink={0} sx={{ my: 1 }}>
            <ReportFilterSidebar
              formik={formik}
              isOpenFilter={openFilter}
              onResetFilter={handleResetFilter}
              onOpenFilter={handleOpenFilter}
              onCloseFilter={handleCloseFilter}
              onSaveFilter={handleSubmit}
              clubFilter
              deviceFilter
              userFilter
            />
          </Stack>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={5} md={3}>
            <AppVolHours summary={summary} />
          </Grid>
          <Grid item xs={12} sm={5} md={3}>
            <AppDistance summary={summary} session={session} />
          </Grid>
          <Grid item xs={12} sm={5} md={3}>
            <AppFlight summary={summary} />
          </Grid>
          <Grid item xs={12} sm={5} md={3}>
            <AppAeros summary={summary} />
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            <AppReportMap summary={summary} />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <AppPhasesSummary summary={summary} />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <AppTotalPointSummary summary={summary} />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <AppReportGraph summary={summary} />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
